import { Component, Vue } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { Spin, Button } from "ant-design-vue";
import { fmtMsg, GLUtil, ActionItem } from "commonui";
import { AccessLevel, ApplicationViews, MethodologyTypeEnum } from "@/util/enum";
import { MethodologyTypeItemsWithAll } from "@/util/constants";
import MasterPlanState from "@/modules/master-plan";
import { VersionGroupModule } from "@/modules/versiongroup";
import LessonPlanListState from "@/modules/lesson-plan-list";
import UnitInfo from "../components/unit-info/unit-info.vue";
import PageTitle from "@/components/page-title/page-title.vue";
import LessonPlanEditor from "../components/lesson-plan-editor/lesson-plan-editor.vue";
import { AppNavigationLocale, LessonPlanLocale } from "@/locales/localeid";
import { Paths } from "@/util/paths";
import LessonPlanTemplateDownload from "../components/template-download/template-download.vue";
import { ILessonPlanTemplateModel } from "@/models/lesson-plan";
import LessonPlanTemplateState from "@/modules/lesson-plan-template";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";

@Component({
	components: {
		"a-spin": Spin,
		"a-button": Button,
		PageTitle,
		UnitInfo,
		LessonPlanEditor,
		ActionItem,
		LessonPlanTemplateDownload
	}
})
export default class MasterPlanManagement extends Vue {
	private masterPlanModule = getModule(MasterPlanState);
	private versionGroupModule = VersionGroupModule;
	private lessonPlanListModule = getModule(LessonPlanListState);
	public view = ApplicationViews.LessonPlanVersion;
	private lessonPlanTemplateModule = getModule(LessonPlanTemplateState);
	public accessLevel = AccessLevel;

	mounted(): void {
		this.versionGroupModule.setDisabled(false);
	}

	destroyed(): void {
		this.versionGroupModule.setDisabled(true);
	}

	get loading(): boolean {
		return this.masterPlanModule.loading || this.lessonPlanListModule.loading;
	}

	get lessonPlanLocale(): any {
		return LessonPlanLocale;
	}

	get appNavigationLocale() {
		return AppNavigationLocale;
	}

	get unitText(): any {
		return this.masterPlanModule.currentUnit.displayName;
	}

	get methodologyText(): any {
		return MethodologyTypeItemsWithAll.get(this.masterPlanModule.currentMethodology);
	}

	get isREP(): boolean {
		return this.masterPlanModule.currentMethodology === MethodologyTypeEnum.REP;
	}

	get goToListText(): string {
		return this.isREP
			? fmtMsg(LessonPlanLocale.LessonPlanListPlaylists)
			: fmtMsg(AppNavigationLocale.AppNavigationLessonPlanList);
	}

	get pageTitle(): string {
		return this.isREP
			? fmtMsg(LessonPlanLocale.LessonPlanListPlaylistEditor)
			: fmtMsg(LessonPlanLocale.LessonPlanMasterPlanLessonPlanEditorPageTitle);
	}

	get lessonPlanVersionText(): any {
		return this.masterPlanModule.currentLessonPlanVersion.name;
	}

	get lessonPlanText(): any {
		return this.masterPlanModule.lessonPlan.sequence;
	}

	get lessonPlanEditItems(): any {
		return this.masterPlanModule.lessonPlanEditItems;
	}

	get lessonPlanTemplates(): ILessonPlanTemplateModel[] {
		return this.lessonPlanTemplateModule.lessonPlanTemplates;
	}

	get lessonPlanListRouter(): any {
		return {
			path: GLUtil.pathStringify(Paths.LessonPlanListWithParams, {
				versionGroupId: this.$route.params.versionGroupId,
				methodology: this.$route.params.methodology,
				lessonPlanVersionId: this.$route.params.lessonPlanVersionId
			}),
			query: { unitId: `${this.masterPlanModule.currentUnit.id}` }
		};
	}
	get masterPlanRouter(): string {
		return GLUtil.pathStringify(Paths.MasterPlan, {
			versionGroupId: this.$route.params.versionGroupId,
			methodology: this.$route.params.methodology,
			lessonPlanVersionId: this.$route.params.lessonPlanVersionId,
			unitId: this.$route.params.unitId
		});
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	onDownloadTemplate(template: ILessonPlanTemplateModel) {
		const { lessonPlanVersionId, unitId } = this.$route.params;
		this.lessonPlanListModule.downloadLessonPlans({
			template,
			lessonPlanVersionId,
			unitId: parseInt(unitId, 10),
			sequenceId: this.masterPlanModule.lessonPlan.sequence
		});
	}
}
